
import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ArrowUpCircle, ArrowDownCircle, RefreshCw } from 'lucide-react';
const apiBase = process.env.REACT_APP_API;

// Function to format currency values
const formatCurrency = (value) => {
  const num = parseFloat(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

export default function BankRevenueStats({startDate, setStartDate, endDate, setEndDate}) {
  const [bankData, setBankData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('bank'); // Default sort by bank name
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchBankData();
  }, [startDate, endDate]);

  const fetchBankData = async () => {
    setIsLoading(true);
    try {
      // Get the current date parameters from the URL
      const urlParams = new URLSearchParams(window.location.search);
      
      const response = await fetch(`${apiBase}/analytics/dashboard/bank/stats?startDate=${startDate}&endDate=${endDate}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setBankData(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch bank data. Please try again later.');
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortedData = [...bankData].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'bank') {
      comparison = a.bank.localeCompare(b.bank);
    } else if (sortBy === 'totalRevenue') {
      comparison = parseFloat(a.totalRevenue) - parseFloat(b.totalRevenue);
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Transform the data for the chart
  const chartData = sortedData.map(item => ({
    bank: item.bank,
    totalRevenue: parseFloat(item.totalRevenue)
  }));

  // Calculate total revenue
  const totalRevenue = bankData.reduce((sum, bank) => sum + parseFloat(bank.totalRevenue), 0);

  // Find the bank with the highest revenue
  const topBank = bankData.length > 0 ? 
    bankData.reduce((prev, current) => 
      parseFloat(prev.totalRevenue) > parseFloat(current.totalRevenue) ? prev : current
    ) : 
    { bank: 'N/A', totalRevenue: '0.00' };

  return (
    <div className=" bg-gray-50 pt-16 min-h-screen">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row  justify-between items-start sm:items-center gap-8 sm:gap-0">
          <h1 className="text-2xl font-bold text-gray-800">Bank Revenue Dashboard</h1>
          <button 
            onClick={fetchBankData} 
            className="flex items-center px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh Data
          </button>
        </div>
        {error && <div className="mt-2 text-red-500">{error}</div>}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-gray-500 text-sm">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalRevenue)}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-gray-500 text-sm">Number of Banks</p>
              <p className="text-2xl font-bold text-gray-800">{bankData.length}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-gray-500 text-sm">Top Performing Bank</p>
              <p className="text-2xl font-bold text-gray-800">{topBank.bank}</p>
              <p className="text-lg text-gray-600">{formatCurrency(topBank.totalRevenue)}</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Revenue Comparison</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bank" />
                  <YAxis 
                    tickFormatter={(value) => 
                      new Intl.NumberFormat('en-US', {
                        notation: 'compact',
                        compactDisplay: 'short'
                      }).format(value)
                    }
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(value) => `Bank: ${value}`}
                  />
                  <Legend />
                  <Bar dataKey="totalRevenue" name="Total Revenue" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6 pb-3">
              <h2 className="text-xl font-semibold text-gray-800">Bank Revenue Details</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('bank')}
                    >
                      <div className="flex items-center">
                        Bank
                        {sortBy === 'bank' && (
                          sortDirection === 'asc' ? 
                            <ArrowUpCircle size={16} className="ml-1" /> : 
                            <ArrowDownCircle size={16} className="ml-1" />
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('totalRevenue')}
                    >
                      <div className="flex items-center">
                        Total Revenue
                        {sortBy === 'totalRevenue' && (
                          sortDirection === 'asc' ? 
                            <ArrowUpCircle size={16} className="ml-1" /> : 
                            <ArrowDownCircle size={16} className="ml-1" />
                        )}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedData.map((bank, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {bank.bank}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(bank.totalRevenue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {totalRevenue ? ((parseFloat(bank.totalRevenue) / totalRevenue) * 100).toFixed(2) + '%' : '0%'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}